import { RefObject, useEffect, useRef } from 'react'

interface Options {
    enabled: boolean
    threshold?: number
    maxSpeed?: number
    minIntervalMs?: number
    axis?: 'x' | 'y' | 'both'
}

export const useDndAutoScroll = (
    ref: RefObject<HTMLElement | null>,
    {
        enabled,
        threshold = 80,
        maxSpeed = 16,
        minIntervalMs = 16,
        axis = 'both',
    }: Options,
) => {
    const pointerRef = useRef<{ x: number; y: number } | null>(null)
    const rafIdRef = useRef<number | null>(null)
    const lastScrollAtRef = useRef<number>(0)

    useEffect(() => {
        if (!enabled) return

        const handlePointerMove = (e: PointerEvent) => {
            pointerRef.current = { x: e.clientX, y: e.clientY }
        }

        const tick = () => {
            const el = ref.current
            const pointer = pointerRef.current
            if (!el || !pointer) {
                rafIdRef.current = requestAnimationFrame(tick)
                return
            }

            const rect = el.getBoundingClientRect()
            const now = Date.now()
            const insideHoriz = pointer.x >= rect.left && pointer.x <= rect.right
            const insideVert = pointer.y >= rect.top && pointer.y <= rect.bottom

            let dx = 0
            if (axis !== 'y' && insideHoriz && insideVert) {
                const leftDist = pointer.x - rect.left
                const rightDist = rect.right - pointer.x
                if (leftDist < threshold && leftDist < rightDist) {
                    dx = -maxSpeed * (1 - leftDist / threshold)
                } else if (rightDist < threshold) {
                    dx = maxSpeed * (1 - rightDist / threshold)
                }
            }

            let dy = 0
            if (axis !== 'x' && insideHoriz && insideVert) {
                const topDist = pointer.y - rect.top
                const bottomDist = rect.bottom - pointer.y
                if (topDist < threshold && topDist < bottomDist) {
                    dy = -maxSpeed * (1 - topDist / threshold)
                } else if (bottomDist < threshold) {
                    dy = maxSpeed * (1 - bottomDist / threshold)
                }
            }

            if ((dx !== 0 || dy !== 0) && now - lastScrollAtRef.current >= minIntervalMs) {
                lastScrollAtRef.current = now
                if (dx !== 0) el.scrollLeft += dx
                if (dy !== 0) el.scrollTop += dy
            }

            rafIdRef.current = requestAnimationFrame(tick)
        }

        window.addEventListener('pointermove', handlePointerMove, { passive: true })
        rafIdRef.current = requestAnimationFrame(tick)

        return () => {
            window.removeEventListener('pointermove', handlePointerMove)
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current)
                rafIdRef.current = null
            }
            pointerRef.current = null
        }
    }, [enabled, threshold, maxSpeed, minIntervalMs, axis, ref])
}
